import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Divider, Paper, Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TvIcon from '@mui/icons-material/Tv';
import { db } from '../../../firebase/config';
import { callClaude } from '../../../utils/claudeProxy';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';

const accent = '#E05500';

function buildPrompt(session, responses, brief, materials) {
  const questions = session.config?.questions || [];
  const groups = session.config?.groups || [];

  const responsesByQuestion = questions.map((q, qi) => {
    const answers = responses
      .map(r => ({
        name:  r.displayName,
        group: groups.find(g => g.id === r.groupId)?.name || r.groupId,
        answer: r.answers?.[qi],
        stars: (r.answerStars?.[qi] || []).length,
        rank:  r.answerRanks?.[qi] || null,
      }))
      .filter(a => a.answer !== undefined && a.answer !== '');
    return { question: q.text, type: q.type, answers };
  });

  const rankLabel = { gold: '🥇 GULD', silver: '🥈 SILVER', bronze: '🥉 BRONS' };

  const summaryText = responsesByQuestion
    .map(({ question, answers }) => {
      const lines = answers
        .sort((a, b) => (b.stars || 0) - (a.stars || 0))
        .map(a => {
          let prefix = `  - ${a.group} / ${a.name}`;
          if (a.rank) prefix += ` [${rankLabel[a.rank]}]`;
          else if (a.stars > 0) prefix += ` [${a.stars} röst${a.stars > 1 ? 'er' : ''}]`;
          return `${prefix}: ${a.answer}`;
        }).join('\n');
      return `FRÅGA: ${question}\nSVAR (sorterade efter röster, rankade svar markerade):\n${lines || '  (inga svar)'}`;
    })
    .join('\n\n');

  let contextSection = '';

  if (brief) {
    contextSection += `\n\n---\nFASILITATORBRIEF (bakgrund och syfte med workshopen):\n${brief}\n---`;
  }

  if (materials && materials.length > 0) {
    const textMaterials = materials.filter(m => m.content);
    const fileMaterials = materials.filter(m => m.url && !m.content);

    if (textMaterials.length > 0) {
      contextSection += '\n\n---\nKONTEXTMATERIAL:';
      textMaterials.forEach(m => {
        const label = [m.name, m.description].filter(Boolean).join(' — ');
        contextSection += `\n\n[${label}]\n${m.content}`;
      });
      contextSection += '\n---';
    }

    if (fileMaterials.length > 0) {
      contextSection += '\n\n---\nÖVRIGA BIFOGADE FILER (kan ej läsas direkt men är relevanta för kontexten):';
      fileMaterials.forEach(m => {
        const label = [m.name, m.description].filter(Boolean).join(' — ');
        contextSection += `\n- ${label} (${m.type})`;
      });
      contextSection += '\n---';
    }
  }

  return `Du är en erfaren workshopfacilitator och organisationskonsult. Nedan följer alla svar från en workshop med titeln "${session.title}".${contextSection}

${summaryText}

Notera: Svar markerade med [🥇 GULD], [🥈 SILVER], [🥉 BRONS] är rankade som de viktigaste av facilitatorn. Svar med röster är prioriterade av deltagarna. Vikta dessa svar tyngre i din analys — de speglar gruppens samlade bedömning om vad som är mest betydelsefullt.

Analysera svaren och återge ENBART ett JSON-objekt med följande struktur (inga kodblock, bara JSON):
{
  "konklusion": "En sammanhållen, tänkvärd konklusion av workshopen i 2-4 meningar.",
  "teman": ["tema 1", "tema 2", "tema 3"],
  "styrkör": ["Styrka/insikt som framkom"],
  "utmaningar": ["Utmaning/spänning som framkom"],
  "forslagNastaSteg": [
    { "steg": "Konkret nästa steg", "prioritet": "hög|medel|låg" }
  ],
  "actions": [
    { "action": "Konkret handling att genomföra", "ansvarig": "roll/grupp eller tom sträng" }
  ]
}`;
}

/**
 * groupId / groupName — valfritt. Om satta körs analysen enbart på den gruppens svar
 * och resultatet sparas till workshops/{session.id}.aiAnalyses.{groupId}.
 * onDisplayGroup(groupId) — callback för att visa gruppen på HDMI-display.
 */
export default function WorkshopAIAnalysis({ session, responses, groupId, groupName, onDisplayGroup }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [materials, setMaterials] = useState([]);
  const [showFull, setShowFull] = useState(false);

  // Ladda sparad analys — per grupp eller hela workshopen
  useEffect(() => {
    if (groupId && session?.aiAnalyses?.[groupId]) {
      setResult(session.aiAnalyses[groupId]);
    } else if (!groupId && session?.aiAnalysis) {
      setResult(session.aiAnalysis);
    }
  }, [groupId, session?.aiAnalyses, session?.aiAnalysis]);

  useEffect(() => {
    if (!session?.id) return;
    getDocs(collection(db, 'workshops', session.id, 'contextMaterials'))
      .then(snap => setMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [session?.id]);

  const analyze = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const brief = session.aiContext?.brief || '';
      // Filter responses to this group if groupId is provided
      const filteredResponses = groupId
        ? responses.filter(r => r.groupId === groupId)
        : responses;
      const prompt = buildPrompt(session, filteredResponses, brief, materials);
      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 2000,
        messages: [{ role: 'user', content: prompt }],
      });
      const raw = data.content?.[0]?.text || '';
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      if (session?.id) {
        const key = groupId ? `aiAnalyses.${groupId}` : 'aiAnalysis';
        await updateDoc(doc(db, 'workshops', session.id), { [key]: parsed });
      }
    } catch (e) {
      setError('Analysen misslyckades.');
    } finally {
      setLoading(false);
    }
  };

  const priorityColor = (p) => p === 'hög' ? '#ef4444' : p === 'medel' ? '#f59e0b' : '#6b7280';

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <AutoAwesomeIcon sx={{ color: accent }} />
        <Typography fontWeight={700} fontSize="1rem">
          AI-analys{groupName ? ` — ${groupName}` : ''}
        </Typography>
        {result && onDisplayGroup && (
          <Button
            size="small" variant="outlined"
            startIcon={<TvIcon sx={{ fontSize: 14 }} />}
            onClick={() => onDisplayGroup(groupId)}
            sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.72rem', borderColor: accent, color: accent, px: 1.25 }}
          >
            Visa på HDMI
          </Button>
        )}
      </Box>

      {!result && (
        <Button
          variant="outlined"
          onClick={analyze}
          disabled={loading || responses.length === 0}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: accent }} /> : <AutoAwesomeIcon />}
          sx={{ borderColor: accent, color: accent, textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Analyserar...' : 'Analysera med AI'}
        </Button>
      )}

      {error && (
        <Typography color="error" fontSize="0.82rem" mt={1}>{error}</Typography>
      )}

      {result && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Vy-toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={showFull ? 'Dölj facilitatorsdetaljer — visar bara konklusion och teman' : 'Visa hela analysen inkl. utmaningar, nästa steg och actions'}>
              <Button
                size="small"
                variant="outlined"
                startIcon={showFull ? <LockOpenIcon sx={{ fontSize: 15 }} /> : <LockIcon sx={{ fontSize: 15 }} />}
                onClick={() => setShowFull(v => !v)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  borderColor: showFull ? accent : 'divider',
                  color: showFull ? accent : 'text.secondary',
                }}
              >
                {showFull ? 'Facilitatorvy (dold för grupp)' : 'Helgruppsvy'}
              </Button>
            </Tooltip>
            <Typography fontSize="0.7rem" color="text.disabled">
              {showFull
                ? 'Utmaningar, nästa steg och actions visas — dölj skärmen för gruppen'
                : 'Konklusion + teman visas — säkert att projicera'}
            </Typography>
          </Box>

          {/* Konklusion — alltid synlig */}
          <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, borderLeft: `4px solid ${accent}` }}>
            <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={0.5}>
              KONKLUSION
            </Typography>
            <Typography fontSize="0.95rem" sx={{ lineHeight: 1.7 }}>{result.konklusion}</Typography>
          </Paper>

          {/* Teman — alltid synliga */}
          {result.teman?.length > 0 && (
            <Box>
              <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={1}>
                FRAMTRÄDANDE TEMAN
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {result.teman.map((t, i) => (
                  <Box key={i} sx={{ px: 1.5, py: 0.5, bgcolor: accent + '22', color: accent, borderRadius: 1, fontSize: '0.82rem', fontWeight: 600 }}>
                    {t}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Facilitatorsdetaljer — dolda i helgruppsvy ── */}
          {showFull && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1, borderTop: `1px dashed ${accent}44` }}>
              <Typography fontSize="0.68rem" color={accent} fontWeight={700} letterSpacing={1}>
                FACILITATOR — INFÖR SWOT-SESSION
              </Typography>

              {/* Styrkor & Utmaningar */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {result.styrkör?.length > 0 && (
                  <Box>
                    <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={1}>
                      STYRKOR / INSIKTER
                    </Typography>
                    {result.styrkör.map((s, i) => (
                      <Typography key={i} fontSize="0.85rem" sx={{ mb: 0.5, '&:before': { content: '"→ "', color: '#4ade80' } }}>
                        {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {result.utmaningar?.length > 0 && (
                  <Box>
                    <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={1}>
                      UTMANINGAR
                    </Typography>
                    {result.utmaningar.map((u, i) => (
                      <Typography key={i} fontSize="0.85rem" sx={{ mb: 0.5, '&:before': { content: '"→ "', color: '#f59e0b' } }}>
                        {u}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Nästa steg */}
              {result.forslagNastaSteg?.length > 0 && (
                <Box>
                  <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={1}>
                    FÖRSLAG NÄSTA STEG
                  </Typography>
                  {result.forslagNastaSteg.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: priorityColor(s.prioritet), mt: '6px', flexShrink: 0 }} />
                      <Box>
                        <Typography fontSize="0.88rem">{s.steg}</Typography>
                        <Typography fontSize="0.72rem" color="text.secondary">Prioritet: {s.prioritet}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Actions */}
              {result.actions?.length > 0 && (
                <Box>
                  <Typography color="text.secondary" fontSize="0.7rem" fontWeight={700} letterSpacing={1} mb={1}>
                    ACTIONS
                  </Typography>
                  {result.actions.map((a, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography fontSize="0.85rem" sx={{ flex: 1 }}>{a.action}</Typography>
                      {a.ansvarig && (
                        <Typography fontSize="0.72rem" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{a.ansvarig}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Button
            size="small"
            onClick={analyze}
            disabled={loading}
            sx={{ color: 'text.secondary', textTransform: 'none', alignSelf: 'flex-start' }}
          >
            Kör om analysen
          </Button>
        </Box>
      )}
    </Box>
  );
}
